import { FIREBALL, SHOCKWAVE, TELEPORT } from '@blorbs/common';
import type { BotDifficulty, PlayerState, RoundPhase } from '@blorbs/common';
import type { WorldView } from './net';

export interface HudActions {
  bots: (count: number, difficulty: BotDifficulty) => void;
  /** tapped the shockwave card */
  shockwave: () => void;
  /** tapped the teleport card */
  teleport: () => void;
}

interface AbilityEl {
  root: HTMLElement;
  cd: HTMLElement;
  secs: HTMLElement;
  total: number;
  lastText: string;
}

/** DOM overlay: phase banner, scoreboard, controls hint. */
export class Hud {
  private banner = document.getElementById('banner')!;
  private scores = document.getElementById('scores')!;
  private scoreboard = document.getElementById('scoreboard')!;
  private ping = document.getElementById('ping')!;
  private abilities: Record<'fire' | 'shock' | 'tp', AbilityEl>;
  private botsCount = document.getElementById('bots-count')!;
  private botsDifficulty = document.getElementById('bots-difficulty') as HTMLSelectElement;
  private bots = 0;
  private lastBanner = '';
  private lastScores = '';
  private lastPing = '';

  constructor(isTouch: boolean, actions: HudActions) {
    document.getElementById('bots-plus')!.addEventListener('click', () => actions.bots(this.bots + 1, this.difficulty()));
    document.getElementById('bots-minus')!.addEventListener('click', () => actions.bots(this.bots - 1, this.difficulty()));
    this.botsDifficulty.addEventListener('change', () => actions.bots(this.bots, this.difficulty()));

    const ability = (id: string, total: number): AbilityEl => {
      const root = document.getElementById(id)!;
      return { root, cd: root.querySelector('.cd')!, secs: root.querySelector('.secs')!, total, lastText: '' };
    };
    this.abilities = {
      fire: ability('ab-fire', FIREBALL.cooldownMs),
      shock: ability('ab-shock', SHOCKWAVE.cooldownMs),
      tp: ability('ab-tp', TELEPORT.cooldownMs),
    };
    // Cards double as buttons (the only way to reach these on touch).
    this.abilities.shock.root.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      actions.shockwave();
    });
    this.abilities.tp.root.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      actions.teleport();
    });
    if (isTouch) document.body.classList.add('touch');

    const helpCard = document.getElementById('help-card')!;
    helpCard.innerHTML = isTouch
      ? '<b>Move</b> drag on the left half<br><b>Shoot</b> tap anywhere else<br><b>Shockwave</b> tap its card<br><b>Teleport</b> tap its card (blinks the way you face)'
      : '<b>Move</b> WASD / arrows<br><b>Aim</b> mouse<br><b>Shoot</b> left click<br><b>Shockwave</b> right click — shoves everyone nearby, blocks fireballs<br><b>Teleport</b> space — blinks toward the mouse';
    document.getElementById('help-toggle')!.addEventListener('click', () => helpCard.classList.toggle('hidden'));

    const toggle = document.getElementById('scoreboard-toggle')!;
    const header = this.scoreboard.querySelector('h3')!;
    header.addEventListener('click', () => {
      const collapsed = this.scoreboard.classList.toggle('collapsed');
      toggle.textContent = collapsed ? '[+]' : '[−]';
    });
  }

  update(view: WorldView, myId: string): void {
    this.setBanner(...bannerFor(view, myId));
    this.setScores(view.players, myId);
    const bots = view.players.filter((p) => p.isBot).length;
    if (bots !== this.bots) {
      this.bots = bots;
      this.botsCount.textContent = String(bots);
    }
  }

  /** Cooldown display: `until` values are server times; fraction remaining drives the radial sweep. */
  setCooldowns(serverNow: number, until: { fire: number; shock: number; tp: number }): void {
    for (const key of ['fire', 'shock', 'tp'] as const) {
      const a = this.abilities[key];
      const remaining = Math.max(0, until[key] - serverNow);
      const ready = remaining <= 0;
      a.root.classList.toggle('ready', ready);
      if (ready) continue;
      a.cd.style.setProperty('--p', String(Math.min(1, remaining / a.total)));
      const text = remaining >= 1000 ? String(Math.ceil(remaining / 1000)) : (remaining / 1000).toFixed(1);
      if (text !== a.lastText) {
        a.lastText = text;
        a.secs.textContent = text;
      }
    }
  }

  difficulty(): BotDifficulty {
    return this.botsDifficulty.value as BotDifficulty;
  }

  setPing(rtt: number, jitter: number, route = ''): void {
    const key = `${rtt}|${jitter}|${route}`;
    if (key === this.lastPing) return;
    this.lastPing = key;
    const ms = rtt > 0 ? `${rtt}${jitter > 25 ? `±${Math.round(jitter / 2)}` : ''} ms` : '…';
    this.ping.textContent = ms + (route ? ` · ${route}` : '');
    this.ping.classList.toggle('bad', rtt > 180);
  }

  private setBanner(text: string, big: boolean): void {
    const key = `${big}|${text}`;
    if (key === this.lastBanner) return;
    this.lastBanner = key;
    this.banner.textContent = text;
    this.banner.classList.toggle('hidden', text === '');
    this.banner.classList.toggle('big', big);
  }

  private setScores(players: PlayerState[], myId: string): void {
    const sorted = [...players].sort((a, b) => b.score - a.score || b.kos - a.kos || a.name.localeCompare(b.name));
    const key = sorted.map((p) => `${p.id}:${p.score}:${p.kos}:${p.alive}`).join(',');
    if (key === this.lastScores) return;
    this.lastScores = key;
    this.scores.replaceChildren(
      ...sorted.map((p) => {
        const li = document.createElement('li');
        li.className = (p.id === myId ? 'me ' : '') + (p.alive ? '' : 'dead');
        const name = document.createElement('span');
        name.textContent = (p.isDummy ? '🎯 ' : p.isBot ? '🤖 ' : '') + p.name;
        const score = document.createElement('span');
        score.textContent = `${p.score}`;
        score.title = `${p.kos} KOs`;
        li.append(name, score);
        return li;
      }),
    );
  }
}

function bannerFor(view: WorldView, myId: string): [string, boolean] {
  const secondsLeft = view.phaseEndsAt ? Math.max(0, Math.ceil((view.phaseEndsAt - view.serverNow) / 1000)) : 0;
  const me = view.players.find((p) => p.id === myId);
  const phase: RoundPhase = view.phase;
  switch (phase) {
    case 'lobby': {
      const combatants = view.players.filter((p) => !p.isDummy).length;
      if (combatants >= 2) return ['', false];
      return [view.players.some((p) => p.isBot) ? 'Practice — add a friend or a fighting 🤖 to start rounds' : 'Waiting for a friend — or add a 🤖', false];
    }
    case 'countdown':
      return [secondsLeft <= 1 ? 'GO!' : `${secondsLeft - 1}`, true];
    case 'playing':
      if (me && !me.alive) return ['You got knocked out — watch the round', false];
      return ['', false];
    case 'results': {
      const winner = view.players.find((p) => p.id === view.winnerId);
      if (!winner) return ['Nobody survived!', true];
      return [winner.id === myId ? 'You win!' : `${winner.name} wins!`, true];
    }
  }
}
